import { IBerryCartLine, IBerryRecommendedItem } from 'src/models/berry.model';
import { IChatMessage } from '../types';

/**
 * Keeps the Berry chat across a page refresh (E2E B85: a refresh threw the
 * whole conversation away while Redis kept the session for 4 hours).
 *
 * sessionStorage, not localStorage: it lives as long as the browser tab, so
 * a refresh restores the chat but a new tab — or a closed browser — starts a
 * fresh order. Holds only what the widget already shows on screen; nothing
 * here is sent anywhere. Every access is guarded: storage can be full,
 * disabled (private mode) or hold an older shape.
 */
export const BERRY_CHAT_STORAGE_KEY = 'berry-chat-v1';
const MAX_STORED_MESSAGES = 100;

export interface IBerryStoredChat {
  sessionId: string;
  messages: IChatMessage[];
  berryCartId: number | null;
  addedItemIds: number[];
  trayItems: IBerryRecommendedItem[] | null;
  trayDismissed: boolean;
  seenItems: IBerryRecommendedItem[];
  cartLines: IBerryCartLine[];
  checkoutLocation: string | null;
}

export function loadBerryChat(): IBerryStoredChat | null {
  try {
    const raw = window.sessionStorage.getItem(BERRY_CHAT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.sessionId !== 'string' || !Array.isArray(parsed.messages)) {
      return null;
    }
    return {
      sessionId: parsed.sessionId,
      messages: parsed.messages,
      berryCartId: typeof parsed.berryCartId === 'number' ? parsed.berryCartId : null,
      addedItemIds: Array.isArray(parsed.addedItemIds) ? parsed.addedItemIds : [],
      trayItems: Array.isArray(parsed.trayItems) ? parsed.trayItems : null,
      trayDismissed: Boolean(parsed.trayDismissed),
      seenItems: Array.isArray(parsed.seenItems) ? parsed.seenItems : [],
      cartLines: Array.isArray(parsed.cartLines) ? parsed.cartLines : [],
      checkoutLocation: typeof parsed.checkoutLocation === 'string' ? parsed.checkoutLocation : null,
    };
  } catch {
    return null;
  }
}

export function saveBerryChat(state: IBerryStoredChat): void {
  try {
    window.sessionStorage.setItem(
      BERRY_CHAT_STORAGE_KEY,
      JSON.stringify({ ...state, messages: state.messages.slice(-MAX_STORED_MESSAGES) }),
    );
  } catch {
    // Storage full or blocked — the chat still works, it just won't survive a refresh.
  }
}

export function clearBerryChat(): void {
  try {
    window.sessionStorage.removeItem(BERRY_CHAT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** "12:00 PM" / "11:30 am" / "noon" → "12:00:00", or null when Berry's time
 * can't be read. The site's checkout matches PickupSlotDetails.time exactly
 * against capacity/available-slots values, which are "HH:MM:SS" — the same
 * shape the edit-order flow writes from promise_time.split(' ')[1]. */
export function toSlotTime(eventTime: string | null | undefined): string | null {
  if (!eventTime) return null;
  const t = eventTime.trim().toLowerCase();
  if (t === 'noon') return '12:00:00';
  const m = t.match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s*m\.?$/);
  if (!m) return null;
  const clockHour = Number(m[1]);
  const minutes = m[2] ?? '00';
  if (clockHour < 1 || clockHour > 12 || Number(minutes) > 59) return null;
  let hours = clockHour % 12;
  if (m[3] === 'p') hours += 12;
  return `${String(hours).padStart(2, '0')}:${minutes}:00`;
}
